/** A shared, authenticated access snapshot. The backend still enforces every gate. */
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { getEntitlements } from '../billingApi';

const EntitlementsContext = createContext(null);
export const ACCESS_FRESH_MS = 5 * 60 * 1000;

export function EntitlementsProvider({ user, children }) {
  const uid = user?.uid || '';
  const userRef = useRef(user);
  userRef.current = user;
  const requestRef = useRef(null);
  const snapshotRef = useRef(null);
  const [state, setState] = useState({ uid: '', entitlements: null, ready: false, error: '' });

  // Stable across snapshots and Firebase token refreshes, keyed by account identity.
  const refresh = useCallback(({ force = true } = {}) => {
    const currentUser = userRef.current;
    if (!currentUser?.uid) return Promise.resolve(null);
    const requestUid = currentUser.uid;
    if (requestRef.current?.uid === requestUid) return requestRef.current.promise;
    const cached = snapshotRef.current;
    if (!force && cached?.uid === requestUid && Date.now() - cached.at < ACCESS_FRESH_MS) return Promise.resolve(cached.value);
    const request = { uid: requestUid };
    request.promise = getEntitlements(currentUser).then((value) => {
      if (userRef.current?.uid === requestUid) {
        snapshotRef.current = { uid: requestUid, value, at: Date.now() };
        setState({ uid: requestUid, entitlements: value, error: '', ready: true });
      }
      return value;
    }).catch((error) => {
      if (userRef.current?.uid === requestUid) {
        // Back off focus events on failures as well; explicit Retry bypasses freshness.
        snapshotRef.current = { uid: requestUid, value: null, at: Date.now() };
        setState({ uid: requestUid, entitlements: null, error: error.message || 'Product access could not be loaded.', ready: true });
      }
      return null;
    }).finally(() => { if (requestRef.current === request) requestRef.current = null; });
    requestRef.current = request;
    return request.promise;
  }, []);

  useEffect(() => {
    snapshotRef.current = null;
    if (!uid) { setState({ uid: '', entitlements: null, ready: true, error: '' }); return undefined; }
    void refresh({ force: false });
    const onReturn = () => { if (document.visibilityState !== 'hidden') void refresh({ force: false }); };
    window.addEventListener('focus', onReturn);
    document.addEventListener('visibilitychange', onReturn);
    return () => {
      window.removeEventListener('focus', onReturn);
      document.removeEventListener('visibilitychange', onReturn);
    };
  }, [uid, refresh]);

  // Expiration is a meaningful refresh boundary; don't poll every minute.
  const trialEndsAt = Number(state.entitlements?.scout?.trialEndsAt || 0);
  const whishEndsAt = state.entitlements?.billing?.provider === 'whish' ? Number(state.entitlements?.billing?.currentPeriodEnd || 0) : 0;
  const expiresAt = [trialEndsAt, whishEndsAt].filter((value) => value > Date.now()).sort((a, b) => a - b)[0] || 0;
  useEffect(() => {
    if (state.uid !== uid || expiresAt <= Date.now()) return undefined;
    const timer = window.setTimeout(() => void refresh(), Math.min(expiresAt - Date.now() + 100, 2147483647));
    return () => window.clearTimeout(timer);
  }, [expiresAt, state.uid, uid, refresh]);

  const value = useMemo(() => ({ ...state, ready: state.uid === uid && state.ready, refresh }), [state, uid, refresh]);
  return <EntitlementsContext.Provider value={value}>{value.ready ? children : <div className="js-app-blank" aria-hidden="true" />}</EntitlementsContext.Provider>;
}

export function useEntitlements() {
  const context = useContext(EntitlementsContext);
  if (!context) throw new Error('useEntitlements must be used inside EntitlementsProvider');
  return context;
}
